import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Sun,
  Umbrella,
  Coffee,
  Stethoscope,
  BookOpen,
  Info,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { timesheetsApi } from "../api/timesheets";
import { MONTH_NAMES } from "../types";
import { formatAmount } from "@/utils/formatAmount";
import { formatDateTime } from "@/utils/formatDate";
import { DraftHistoryTimeline } from "./DraftHistoryTimeline";

interface PreviewDay {
  day: number;
  date_str: string;
  weekday_short: string;
  is_weekend: boolean;
  is_holiday: boolean;
  leave_label: string;
  other_leave_label: string;
  sick_leave_label: string;
  auto_note: string;
  is_working: boolean;
}

interface TimeRange {
  from: string;
  to: string;
}

interface DayState {
  shift: 7 | 8 | 9 | null;
  ranges: TimeRange[];
  extra_note: string;
}

interface DraftPreviewModalProps {
  open: boolean;
  onClose: () => void;
  employeeId: string;
  employeeName: string;
  year: number;
  month: number;
  /** null = primary contract; UUID = a specific UZ-secondary contract. */
  secondaryContractId?: string | null;
  /** "uz" itp. — only used for labelling when previewing a secondary contract. */
  secondaryContractKind?: string | null;
}

function toMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function rangeHours(from: string, to: string): number {
  if (!from || !to) return 0;
  const mins = toMinutes(to) - toMinutes(from);
  return mins > 0 ? mins / 60 : 0;
}

function formatHours(h: number): string {
  if (h <= 0) return "";
  const r = Math.round(h * 10) / 10;
  const totalMins = Math.round(r * 60);
  const hrs = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return mins === 0 ? `${hrs}h` : `${hrs}:${String(mins).padStart(2, "0")}`;
}

export function DraftPreviewModal({
  open,
  onClose,
  employeeId,
  employeeName,
  year,
  month,
  secondaryContractId,
  secondaryContractKind,
}: DraftPreviewModalProps) {
  const isSecondaryUz = !!secondaryContractId;

  const previewQuery = useQuery({
    queryKey: [
      "timesheets",
      "pdf-preview",
      employeeId,
      year,
      month,
      secondaryContractId ?? null,
    ],
    queryFn: () =>
      timesheetsApi.pdfPreview(year, month, secondaryContractId ?? null, employeeId),
    enabled: open,
    staleTime: 1000 * 30,
  });

  const draftQuery = useQuery({
    queryKey: [
      "timesheets",
      "pdf-draft",
      employeeId,
      year,
      month,
      secondaryContractId ?? null,
    ],
    queryFn: () =>
      timesheetsApi.getPdfDraft(year, month, secondaryContractId ?? null, employeeId),
    enabled: open,
    staleTime: 1000 * 30,
  });

  const isLoading = previewQuery.isLoading || draftQuery.isLoading;
  const preview = previewQuery.data;
  const draft = draftQuery.data;

  const isUz = !isSecondaryUz && !!preview?.is_uz;
  const periodLabel = `${MONTH_NAMES[month]} ${year}`;

  const dayStates = useMemo<Record<string, DayState>>(() => {
    return (draft?.day_states ?? {}) as Record<string, DayState>;
  }, [draft?.day_states]);

  const defaultShift = (draft?.default_shift ?? 8) as 7 | 8 | 9;
  const declaredHours = draft?.declared_hours ?? null;
  const dayUpdatedAt = draft?.day_updated_at ?? {};

  const contractLabel = (() => {
    if (isSecondaryUz) {
      return `Umowa zlecenie (dodatkowa)${secondaryContractKind ? ` — ${secondaryContractKind.toUpperCase()}` : ""}`;
    }
    return isUz ? "Umowa zlecenie (podstawowa)" : "Umowa o pracę";
  })();

  // For UZ-flat brutto calculation, get rate info from the preview payload.
  const contractRate = preview?.secondary_contract?.hourly_gross_rate
    ? Number(preview.secondary_contract.hourly_gross_rate)
    : 0;
  const isMonthlyRate = preview?.secondary_contract?.rate_type === "monthly";

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={`Podgląd roboczej karty — ${employeeName}`}
    >
      <div className="flex flex-col gap-4">
        {/* Info banner */}
        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-start gap-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-3 py-2">
          <Info size={12} className="mt-0.5 flex-shrink-0" />
          <span>
            Widok tylko do odczytu — taki, jak wygląda obecnie edytor pracownika.
            Treść może się jeszcze zmienić zanim pracownik wygeneruje PDF i złoży
            formalną kartę.
          </span>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : previewQuery.isError || draftQuery.isError ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 text-sm text-red-700 dark:text-red-300">
            Nie udało się wczytać danych.
          </div>
        ) : (
          <>
            {/* Period + contract label + default shift (UoP only) */}
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-44">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Okres
                </label>
                <select
                  className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-3 py-2 text-sm cursor-not-allowed"
                  value={`${year}-${month}`}
                  disabled
                >
                  <option value={`${year}-${month}`}>{periodLabel}</option>
                </select>
              </div>
              <div className="flex-1 min-w-56">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Karta godzin dla
                </label>
                <select
                  className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-3 py-2 text-sm cursor-not-allowed"
                  disabled
                  value="x"
                >
                  <option value="x">{contractLabel}</option>
                </select>
              </div>

              {!isUz && !isSecondaryUz && (
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                    Domyślna zmiana{" "}
                    <span className="text-xs text-gray-400 font-normal">
                      (stosowana do wszystkich dni)
                    </span>
                  </label>
                  <div className="flex gap-1">
                    {([7, 8, 9] as const).map((s) => (
                      <button
                        key={s}
                        type="button"
                        disabled
                        className={`px-3 py-2 rounded-md text-sm font-medium border cursor-not-allowed ${
                          defaultShift === s
                            ? "border-[var(--color-primary)] bg-blue-50 dark:bg-blue-900/30 text-[var(--color-primary)] dark:text-blue-300"
                            : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400"
                        }`}
                      >
                        {s}:00–{s + 8}:00
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Legend — only for per-day mode */}
            {!isSecondaryUz && (
              <div className="flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-gray-100 dark:bg-gray-700 inline-block" />
                  Weekend
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-yellow-50 dark:bg-yellow-900/10 inline-block" />
                  Święto
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-purple-50 dark:bg-purple-900/10 inline-block" />
                  L4
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-blue-50 dark:bg-blue-900/10 inline-block" />
                  Urlop
                </span>
              </div>
            )}

            {!draft && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-3 text-sm text-amber-800 dark:text-amber-300">
                Brak zapisanego szkicu dla tego okresu — pracownik nic jeszcze nie wpisał.
              </div>
            )}

            {/* Flat declared-hours panel — secondary UZ only */}
            {isSecondaryUz && draft && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                    Liczba przepracowanych godzin
                    <span className="ml-1 text-xs text-gray-400 font-normal">
                      (deklarowana suma za cały miesiąc)
                    </span>
                  </label>
                  <input
                    type="text"
                    disabled
                    className="block w-40 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-3 py-2 text-sm font-mono text-right cursor-not-allowed"
                    value={declaredHours ?? ""}
                    placeholder="(brak)"
                  />
                </div>
                {(() => {
                  const hours = Number(declaredHours ?? 0);
                  const gross = isMonthlyRate
                    ? (contractRate > 0 ? contractRate.toFixed(2) : "—")
                    : (hours > 0 && contractRate > 0 ? (hours * contractRate).toFixed(2) : "—");
                  return (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm rounded-md bg-gray-50 dark:bg-gray-900/50 px-3 py-2">
                      <div>
                        <div className="text-[11px] uppercase tracking-wide text-gray-500">
                          Godziny
                        </div>
                        <div className="font-mono font-semibold text-gray-900 dark:text-gray-100">
                          {hours > 0 ? hours : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-wide text-gray-500">
                          Stawka
                        </div>
                        <div className="font-mono text-gray-700 dark:text-gray-300">
                          {contractRate > 0 ? `${formatAmount(contractRate)} ${isMonthlyRate ? "zł/mies." : "zł/h"}` : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-wide text-gray-500">
                          Brutto
                        </div>
                        <div className="font-mono font-semibold text-gray-900 dark:text-gray-100">
                          {gross !== "—" ? `${gross} zł` : "—"}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                <p className="text-[11px] text-gray-500 dark:text-gray-400 italic">
                  UZ dodatkowa rozliczana ryczałtem — bez harmonogramu per dzień.
                </p>
              </div>
            )}

            {/* Days table — primary contract only (UoP per-shift, UZ-primary per-range) */}
            {!isSecondaryUz && preview?.days && (
              <div className="max-h-[380px] overflow-y-auto">
                <table className="w-full text-xs border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                      <th className="px-2 py-2 text-left w-16 font-mono text-xs uppercase tracking-widest text-gray-400 dark:text-gray-500">
                        Data
                      </th>
                      <th className="px-2 py-2 text-left w-10 font-mono text-xs uppercase tracking-widest text-gray-400 dark:text-gray-500">
                        Dz.
                      </th>
                      <th className="px-2 py-2 text-center font-mono text-xs uppercase tracking-widest text-gray-400 dark:text-gray-500">
                        {isUz ? "Zakresy godzin" : "Zmiana"}
                      </th>
                      <th className="px-2 py-2 text-left font-mono text-xs uppercase tracking-widest text-gray-400 dark:text-gray-500">
                        Nieobecność / Uwagi
                      </th>
                      {isUz && (
                        <th className="px-2 py-2 text-left w-24 font-mono text-xs uppercase tracking-widest text-gray-400 dark:text-gray-500">
                          Ost. zmiana
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.days as PreviewDay[]).map((d) => {
                      const st = (dayStates[String(d.day)] ?? {
                        shift: defaultShift,
                        ranges: [],
                        extra_note: "",
                      }) as DayState;
                      // UZ pracownik może pracować w święta i weekendy —
                      // jedyne co blokuje wpis to faktyczna nieobecność
                      // (urlop / L4 / urlop inny). Bez warunku is_working.
                      const uzEditable =
                        isUz &&
                        !d.leave_label &&
                        !d.sick_leave_label &&
                        !d.other_leave_label;

                      const rowBg = d.is_weekend
                        ? "bg-gray-50 dark:bg-gray-800/40"
                        : d.is_holiday
                          ? "bg-yellow-50 dark:bg-yellow-900/10"
                          : d.sick_leave_label
                            ? "bg-purple-50 dark:bg-purple-900/10"
                            : d.leave_label || d.other_leave_label
                              ? "bg-blue-50 dark:bg-blue-900/10"
                              : "bg-white dark:bg-gray-800";

                      return (
                        <tr
                          key={d.day}
                          className={`border-t border-gray-100 dark:border-gray-700/50 ${rowBg}`}
                        >
                          {/* Date */}
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <span
                              className={`font-semibold ${
                                d.is_weekend
                                  ? "text-gray-400"
                                  : "text-gray-800 dark:text-gray-200"
                              }`}
                            >
                              {d.date_str}
                            </span>
                          </td>

                          {/* Day name */}
                          <td
                            className={`px-2 py-1.5 ${
                              d.is_weekend
                                ? "text-gray-400"
                                : "text-gray-500 dark:text-gray-400"
                            }`}
                          >
                            {d.weekday_short}
                          </td>

                          {/* Shift (UoP) or time ranges (UZ-primary) */}
                          <td className="px-2 py-1">
                            {isUz ? (
                              uzEditable ? (
                                st.ranges.length > 0 ? (
                                  <div className="flex flex-col gap-1">
                                    {st.ranges.map((r, i) => {
                                      const h =
                                        r.from && r.to
                                          ? rangeHours(r.from, r.to)
                                          : 0;
                                      return (
                                        <div
                                          key={i}
                                          className="flex items-center gap-1"
                                        >
                                          <input
                                            type="time"
                                            value={r.from}
                                            disabled
                                            className="w-[5.5rem] rounded border px-1 py-0.5 text-xs text-center bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 cursor-not-allowed"
                                          />
                                          <span className="text-gray-400 select-none">
                                            –
                                          </span>
                                          <input
                                            type="time"
                                            value={r.to}
                                            disabled
                                            className="w-[5.5rem] rounded border px-1 py-0.5 text-xs text-center bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 cursor-not-allowed"
                                          />
                                          {h > 0 && (
                                            <span className="text-gray-400 w-7 text-right tabular-nums">
                                              {formatHours(h)}
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <span className="text-gray-300 dark:text-gray-600">
                                    (brak zakresów)
                                  </span>
                                )
                              ) : (
                                <span className="text-gray-300 dark:text-gray-600">
                                  —
                                </span>
                              )
                            ) : d.is_working ? (
                              <div className="flex gap-0.5 justify-center">
                                {([7, 8, 9] as const).map((s) => (
                                  <button
                                    key={s}
                                    type="button"
                                    disabled
                                    className={`w-8 h-6 rounded text-xs font-medium cursor-not-allowed ${
                                      st.shift === s
                                        ? "bg-[var(--color-primary)] text-white shadow-sm"
                                        : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                                    }`}
                                  >
                                    {s}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600">
                                —
                              </span>
                            )}
                          </td>

                          {/* Note */}
                          <td className="px-2 py-1">
                            <div className="flex items-center gap-1.5">
                              {d.is_holiday && (
                                <Sun
                                  size={11}
                                  className="text-gray-400 flex-shrink-0"
                                />
                              )}
                              {d.sick_leave_label && !d.is_holiday && (
                                <Stethoscope
                                  size={11}
                                  className="text-gray-400 flex-shrink-0"
                                />
                              )}
                              {d.leave_label &&
                                !d.sick_leave_label &&
                                !d.is_holiday && (
                                  <Umbrella
                                    size={11}
                                    className="text-gray-400 flex-shrink-0"
                                  />
                                )}
                              {d.other_leave_label &&
                                !d.sick_leave_label &&
                                !d.leave_label &&
                                !d.is_holiday && (
                                  <BookOpen
                                    size={11}
                                    className="text-gray-400 flex-shrink-0"
                                  />
                                )}
                              {d.auto_note && (
                                <span className="text-gray-500 dark:text-gray-400 font-medium">
                                  {d.auto_note}
                                </span>
                              )}
                              {(isUz ? uzEditable : d.is_working) && (
                                <input
                                  type="text"
                                  value={st.extra_note ?? ""}
                                  disabled
                                  placeholder="(brak uwagi)"
                                  className="flex-1 min-w-0 bg-transparent border-none outline-none text-xs text-gray-500 dark:text-gray-400 placeholder-gray-300 dark:placeholder-gray-600 cursor-not-allowed"
                                />
                              )}
                              {d.is_weekend && !d.leave_label && !isUz && (
                                <span className="text-gray-300 dark:text-gray-600 flex items-center gap-0.5">
                                  <Coffee size={10} /> wolne
                                </span>
                              )}
                            </div>
                          </td>
                          {isUz && (
                            <td className="px-2 py-1 text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                              {formatDateTime(dayUpdatedAt[String(d.day)], "")}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {draft?.updated_at && (
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Ostatnia zmiana szkicu:{" "}
                {new Date(draft.updated_at).toLocaleString("pl-PL")}
              </p>
            )}

            <DraftHistoryTimeline
              year={year}
              month={month}
              secondaryContractId={secondaryContractId ?? null}
              employeeId={employeeId}
              title="Historia zmian (audyt — kontrola wpisów)"
            />
          </>
        )}

        <div className="flex flex-wrap gap-3">
          <Button variant="secondary" onClick={onClose}>
            Zamknij
          </Button>
        </div>
      </div>
    </Modal>
  );
}
